import React, { useEffect, useMemo, useState } from 'react';
import { Delete as TrashIcon, Edit as PencilIcon, InfoOutlined as InfoIcon } from '@mui/icons-material';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import Button from '../ui/Button';
import { useConfirm } from '../ui/ConfirmDialog';
import { Dropdown } from '../ui/Dropdown';
import { displayAuditRecommendation, displayAuditText, hasInternalAuditId } from '../../utils/kbAuditText';
import {
  applyKbAuditDecisions,
  type KbAuditDecision,
  type KbAuditFinding,
  type KbAuditSourceEdit,
  type KbAuditSourceItem,
} from '../../services/apiService';

const KIND_LABELS: Record<KbAuditFinding['kind'], string> = {
  duplicate_family: 'Duplicate concern',
  filler_variations: 'Filler phrases',
  broad_landmine: 'Broad compliance trigger',
  contradiction: 'Conflicting guidance',
};

const SOURCE_LABELS: Record<KbAuditSourceItem['sourceCollection'], string> = {
  objections: 'Objection',
  faqs: 'FAQ',
  landmines: 'Landmine',
};

const PUBLISH_AS_OPTIONS = [
  { value: 'objection', label: 'Objection' },
  { value: 'faq', label: 'FAQ' },
];

interface SourceDraft {
  question: string;
  answer: string;
  trigger: string;
  triggerVariations: string[];
}

interface Props {
  findings: KbAuditFinding[];
  activeFindingId: string | null;
  onClose: () => void;
  onApplied: (findingIds: string[]) => void;
}

const sourceDraft = (source: KbAuditSourceItem): SourceDraft => ({
  question: source.label || '',
  answer: source.answer || '',
  trigger: source.label || '',
  triggerVariations: source.variations || [],
});

const KbAuditReviewPanel: React.FC<Props> = ({ findings, activeFindingId, onClose, onApplied }) => {
  const confirm = useConfirm();
  const [selectedId, setSelectedId] = useState(activeFindingId || findings[0]?._id || '');
  const [decisions, setDecisions] = useState<Record<string, KbAuditDecision>>({});
  const [editingSourceKey, setEditingSourceKey] = useState<string | null>(null);
  const [sourceDrafts, setSourceDrafts] = useState<Record<string, SourceDraft>>({});
  const [showDetails, setShowDetails] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (activeFindingId) setSelectedId(activeFindingId);
  }, [activeFindingId]);

  useEffect(() => {
    setShowDetails(false);
    setEditingSourceKey(null);
  }, [selectedId]);

  const finding = findings.find((item) => item._id === selectedId) || findings[0];
  const decision = finding ? decisions[finding._id] : undefined;
  const selectedCount = Object.keys(decisions).length;

  const updateDecision = (next: KbAuditDecision | null) => {
    if (!finding) return;
    setDecisions((current) => {
      const copy = { ...current };
      if (next) copy[finding._id] = next;
      else delete copy[finding._id];
      return copy;
    });
    setError(null);
  };

  const defaultMerge = useMemo<KbAuditDecision | null>(() => {
    if (!finding || finding.kind !== 'duplicate_family') return null;
    const preferred = finding.sourceItems[0];
    const combinedAnswer = finding.proposedChange.combinedAnswer || '';
    return {
      findingId: finding._id,
      action: 'merge',
      keepDocumentId: preferred?.documentId,
      resultingType: finding.proposedChange.recommendedType || (preferred?.sourceCollection === 'faqs' ? 'faq' : 'objection'),
      question: preferred?.label || '',
      answer: combinedAnswer && !hasInternalAuditId(combinedAnswer) ? combinedAnswer : preferred?.answer || '',
    };
  }, [finding]);

  const draftKey = (source: KbAuditSourceItem) => `${finding._id}:${source.documentId}`;

  const beginSourceEdit = (source: KbAuditSourceItem) => {
    const key = draftKey(source);
    setSourceDrafts((current) => current[key] ? current : { ...current, [key]: sourceDraft(source) });
    setEditingSourceKey(key);
    if (decision?.action === 'delete') updateDecision(null);
  };

  const stageSourceEdit = (source: KbAuditSourceItem, nextDraft: SourceDraft) => {
    const edit: KbAuditSourceEdit = source.sourceCollection === 'landmines'
      ? {
          documentId: source.documentId,
          trigger: nextDraft.trigger,
          triggerVariations: nextDraft.triggerVariations,
        }
      : {
          documentId: source.documentId,
          question: nextDraft.question,
          answer: nextDraft.answer,
        };

    if (decision?.action === 'keep_distinct') {
      const sourceEdits = [
        ...(decision.sourceEdits || []).filter((item) => item.documentId !== source.documentId),
        edit,
      ];
      updateDecision({ ...decision, sourceEdits });
      return;
    }

    updateDecision({
      findingId: finding._id,
      action: 'edit',
      ...edit,
      ...(decision?.action === 'edit' && decision.removeVariations
        ? { removeVariations: decision.removeVariations }
        : {}),
    });
  };

  const updateSourceDraft = (source: KbAuditSourceItem, patch: Partial<SourceDraft>) => {
    const key = draftKey(source);
    const nextDraft = { ...(sourceDrafts[key] || sourceDraft(source)), ...patch };
    setSourceDrafts((current) => ({ ...current, [key]: nextDraft }));
    stageSourceEdit(source, nextDraft);
  };

  const stageDelete = (source: KbAuditSourceItem) => {
    setEditingSourceKey(null);
    updateDecision({ findingId: finding._id, action: 'delete', documentId: source.documentId });
  };

  const clearCurrentDecision = () => {
    setEditingSourceKey(null);
    updateDecision(null);
  };

  const apply = async () => {
    const values = Object.values(decisions);
    if (!values.length) return;
    const deleteCount = values.filter((value) => value.action === 'delete').length;
    const approved = await confirm({
      title: `Apply ${values.length} selected change${values.length === 1 ? '' : 's'}?`,
      message: `${deleteCount ? `${deleteCount} selected entr${deleteCount === 1 ? 'y' : 'ies'} will be removed from the live knowledge base. ` : ''}Unselected findings will stay open.`,
      confirmLabel: `Apply ${values.length} selected change${values.length === 1 ? '' : 's'}`,
    });
    if (!approved) return;
    setApplying(true);
    setError(null);
    try {
      const applied = await applyKbAuditDecisions(values);
      const findingIds = applied.map((item) => item.findingId);
      if (findingIds.length !== values.length) throw new Error('Could not confirm every saved change. Refresh the page before trying again.');
      setDecisions({});
      onApplied(findingIds);
    } catch (applyError: any) {
      setError(applyError?.message || 'The changes could not be applied. Review the refreshed finding and try again.');
    } finally {
      setApplying(false);
    }
  };

  if (!finding) return null;

  const publicText = (text: string) => displayAuditText(text, finding.sourceItems);
  const broadSource = finding.kind === 'broad_landmine' ? finding.sourceItems[0] : undefined;
  const suggestedTrigger = finding.kind === 'broad_landmine'
    ? publicText(finding.proposedChange.trigger?.trim() || '')
    : undefined;
  const suggestedTriggerVariations = finding.kind === 'broad_landmine'
    ? (finding.proposedChange.triggerVariations || []).filter(Boolean).map(publicText)
    : [];
  const hasBroadSuggestion = Boolean(
    broadSource
    && suggestedTrigger
    && suggestedTrigger.toLocaleLowerCase() !== broadSource.label.trim().toLocaleLowerCase(),
  );

  const renderSourceEditor = (source: KbAuditSourceItem) => {
    const key = draftKey(source);
    if (editingSourceKey !== key) return null;
    const draft = sourceDrafts[key] || sourceDraft(source);
    return source.sourceCollection === 'landmines' ? (
      <div className="mt-3 space-y-3 border-t border-hairline pt-3">
        <label className="block text-sm text-ink">Specific trigger
          <input
            className="mt-1 w-full rounded-control border border-ink-border bg-surface p-2"
            value={draft.trigger}
            onChange={(event) => updateSourceDraft(source, { trigger: event.target.value })}
          />
        </label>
        <label className="block text-sm text-ink">Trigger variations
          <textarea
            rows={3}
            className="mt-1 w-full rounded-control border border-ink-border bg-surface p-2"
            value={draft.triggerVariations.join('\n')}
            onChange={(event) => updateSourceDraft(source, {
              triggerVariations: event.target.value.split('\n').map((value) => value.trim()).filter(Boolean),
            })}
          />
        </label>
      </div>
    ) : (
      <div className="mt-3 space-y-3 border-t border-hairline pt-3">
        <label className="block text-sm text-ink">Question
          <input
            className="mt-1 w-full rounded-control border border-ink-border bg-surface p-2"
            value={draft.question}
            onChange={(event) => updateSourceDraft(source, { question: event.target.value })}
          />
        </label>
        <label className="block text-sm text-ink">Answer
          <textarea
            rows={4}
            className="mt-1 w-full rounded-control border border-ink-border bg-surface p-2"
            value={draft.answer}
            onChange={(event) => updateSourceDraft(source, { answer: event.target.value })}
          />
        </label>
      </div>
    );
  };

  return (
    <Dialog open onClose={applying ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Review knowledge base health</DialogTitle>
      <DialogContent>
        <div className="grid gap-6 md:grid-cols-[260px_minmax(0,1fr)]">
          <div className="space-y-2 border-r border-hairline pr-4">
            <p className="pb-1 text-xs text-ink-muted">
              {selectedCount} of {findings.length} selected
            </p>
            {findings.map((item) => (
              <button
                key={item._id}
                type="button"
                onClick={() => setSelectedId(item._id)}
                className={`w-full rounded-control border p-3 text-left ${item._id === finding._id ? 'border-signature bg-signature-tint' : 'border-hairline bg-surface hover:bg-surface-subtle'}`}
              >
                <span className="block text-xs text-ink-muted">{KIND_LABELS[item.kind]}</span>
                <span className="mt-1 block text-sm font-medium text-ink">{displayAuditText(item.title, item.sourceItems)}</span>
                {decisions[item._id] && <span className="mt-2 block text-xs text-success">Selected to apply</span>}
              </button>
            ))}
          </div>

          <div className="space-y-5">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-ink-muted">{KIND_LABELS[finding.kind]}</p>
              <h3 className="heading-4 mt-1 text-ink">
                {publicText(finding.title)}
                <button
                  type="button"
                  aria-label={showDetails ? 'Hide finding details' : 'Show finding details'}
                  aria-expanded={showDetails}
                  onClick={() => setShowDetails((current) => !current)}
                  className="ml-2 inline-flex h-7 w-7 items-center justify-center align-middle rounded-control text-ink-muted hover:bg-surface-subtle hover:text-ink focus:outline-none focus:ring-2 focus:ring-signature"
                  title="Why this matters"
                >
                  <InfoIcon style={{ fontSize: '1rem' }} />
                </button>
              </h3>
            </div>

            {showDetails && (
              <div className="rounded-card border border-hairline bg-surface-subtle p-5">
                <p className="text-sm font-medium text-ink">Why this matters</p>
                <p className="mt-2 text-sm leading-body text-ink-secondary">{publicText(finding.impact)}</p>
                <p className="mt-4 text-sm font-medium text-ink">Recommended next step</p>
                <p className="mt-2 text-sm leading-body text-ink-secondary">
                  {displayAuditRecommendation(finding.recommendation, finding.sourceItems)}
                </p>
              </div>
            )}

            <div>
              <p className="text-sm font-medium text-ink">Affected entries</p>
              <div className="mt-2 space-y-2">
                {finding.sourceItems.map((source) => {
                  const deleting = decision?.action === 'delete' && decision.documentId === source.documentId;
                  return (
                    <div
                      key={`${source.sourceCollection}-${source.documentId}`}
                      className={`rounded-control border p-3 ${deleting ? 'border-error bg-error-tint' : 'border-hairline bg-surface'}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <span className="inline-flex rounded-full border border-hairline bg-surface-subtle px-2 py-0.5 text-xs text-ink-muted">
                            {SOURCE_LABELS[source.sourceCollection]}
                          </span>
                          <p className="mt-2 text-sm font-medium text-ink">{source.label}</p>
                          {source.answer && <p className="mt-1 text-sm text-ink-secondary">{source.answer}</p>}
                          {deleting && (
                            <div className="mt-3 rounded-control border border-error bg-surface px-3 py-2">
                              <p className="text-xs font-medium text-error">Selected for deletion</p>
                              <p className="mt-1 text-xs text-ink-secondary">Nothing will be removed until you apply all selected changes.</p>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-1">
                          {deleting ? (
                            <button
                              type="button"
                              onClick={clearCurrentDecision}
                              className="min-h-[36px] rounded-control px-2 text-xs text-ink hover:bg-surface-subtle focus:outline-none focus:ring-2 focus:ring-signature"
                            >
                              Undo
                            </button>
                          ) : (
                            <>
                              <button
                                type="button"
                                aria-label={`Edit ${SOURCE_LABELS[source.sourceCollection]}: ${source.label}`}
                                onClick={() => beginSourceEdit(source)}
                                className="flex h-9 w-9 items-center justify-center rounded-control text-ink-muted hover:bg-surface-subtle hover:text-ink focus:outline-none focus:ring-2 focus:ring-signature"
                                title={`Edit ${SOURCE_LABELS[source.sourceCollection]}`}
                              >
                                <PencilIcon style={{ fontSize: '1.05rem' }} />
                              </button>
                              <button
                                type="button"
                                aria-label={`Delete ${SOURCE_LABELS[source.sourceCollection]}: ${source.label}`}
                                onClick={() => stageDelete(source)}
                                className="flex h-9 w-9 items-center justify-center rounded-control text-ink-muted hover:bg-error-tint hover:text-error focus:outline-none focus:ring-2 focus:ring-error"
                                title={`Delete ${SOURCE_LABELS[source.sourceCollection]}`}
                              >
                                <TrashIcon style={{ fontSize: '1.05rem' }} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      {renderSourceEditor(source)}
                    </div>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-ink-muted">
                {finding.evidence?.totalFires || 0} total fires across {finding.evidence?.distinctCalls || 0} calls in the last 30 days
              </p>
            </div>

            {finding.kind === 'duplicate_family' && decision?.action !== 'delete' && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-ink">Choose what to publish</p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant={decision?.action === 'merge' ? 'primary' : 'secondary'} onClick={() => updateDecision(defaultMerge)}>Merge these entries</Button>
                  <Button
                    variant={decision?.action === 'keep_distinct' ? 'primary' : 'secondary'}
                    onClick={() => updateDecision({
                      findingId: finding._id,
                      action: 'keep_distinct',
                      sourceEdits: decision?.action === 'keep_distinct' ? decision.sourceEdits : [],
                    })}
                  >
                    Keep separate
                  </Button>
                  <Button variant="tertiary" onClick={clearCurrentDecision}>Skip for now</Button>
                </div>
                {decision?.action === 'keep_distinct' && (
                  <p className="text-xs text-ink-muted">Use an entry's edit icon if its wording should change before these remain separate.</p>
                )}
                {decision?.action === 'merge' && (
                  <div className="space-y-3 rounded-card border border-hairline bg-surface p-5">
                    {/* A label wrapping an MUI combobox would forward its clicks to the hidden input, so these name it from above. */}
                    <div className="text-sm text-ink">
                      <label id="kb-audit-keep-family-label" className="mb-1 block">Keep this family</label>
                      <Dropdown
                        labelledBy="kb-audit-keep-family-label"
                        width="field"
                        value={decision.keepDocumentId ?? ''}
                        onChange={(keepDocumentId) => {
                          const preferred = finding.sourceItems.find((source) => source.documentId === keepDocumentId);
                          updateDecision({
                            ...decision,
                            keepDocumentId,
                            question: preferred?.label || decision.question,
                            answer: finding.proposedChange.combinedAnswer && !hasInternalAuditId(finding.proposedChange.combinedAnswer)
                              ? finding.proposedChange.combinedAnswer
                              : preferred?.answer || decision.answer,
                          });
                        }}
                        options={finding.sourceItems.map((source) => ({ value: source.documentId, label: source.label }))}
                      />
                    </div>
                    <div className="text-sm text-ink">
                      <label id="kb-audit-publish-as-label" className="mb-1 block">Publish as</label>
                      <Dropdown
                        labelledBy="kb-audit-publish-as-label"
                        width="field"
                        value={decision.resultingType ?? 'objection'}
                        onChange={(resultingType) => updateDecision({ ...decision, resultingType: resultingType as 'objection' | 'faq' })}
                        options={PUBLISH_AS_OPTIONS}
                      />
                    </div>
                    <label className="block text-sm text-ink">Question<input className="mt-1 w-full rounded-control border border-ink-border bg-surface p-2" value={decision.question || ''} onChange={(event) => updateDecision({ ...decision, question: event.target.value })} /></label>
                    <label className="block text-sm text-ink">Combined answer<textarea rows={5} className="mt-1 w-full rounded-control border border-ink-border bg-surface p-2" value={decision.answer || ''} onChange={(event) => updateDecision({ ...decision, answer: event.target.value })} /></label>
                  </div>
                )}
              </div>
            )}

            {finding.kind === 'filler_variations' && decision?.action !== 'delete' && (
              <div className="space-y-3">
                <div className="rounded-control border border-hairline bg-surface p-3">
                  <p className="text-sm font-medium text-ink">Phrase family after this change</p>
                  <ul className="mt-2 space-y-2 text-sm text-ink-secondary">
                    {[finding.sourceItems[0]?.label, ...(finding.sourceItems[0]?.variations || [])].filter(Boolean).map((variation) => {
                      const removed = (finding.proposedChange.removeVariations || []).includes(variation as string);
                      return (
                        <li key={variation as string} className="flex items-start justify-between gap-3">
                          <span className={removed ? 'text-ink-muted line-through' : ''}>{variation}</span>
                          <span className="flex-shrink-0 text-xs text-ink-muted">{removed ? 'Remove' : 'Keep'}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filler finding actions">
                  <Button
                    variant={decision?.action === 'edit' && Boolean(decision.removeVariations?.length) ? 'primary' : 'secondary'}
                    onClick={() => updateDecision({
                      findingId: finding._id,
                      action: 'edit',
                      documentId: finding.sourceItems[0]?.documentId,
                      removeVariations: finding.proposedChange.removeVariations || [],
                    })}
                  >
                    Remove filler phrases
                  </Button>
                  <Button variant="tertiary" onClick={clearCurrentDecision}>Skip for now</Button>
                </div>
              </div>
            )}

            {finding.kind === 'broad_landmine' && decision?.action !== 'delete' && (
              <div className="space-y-3">
                {hasBroadSuggestion && broadSource && suggestedTrigger && (
                  <div className="rounded-card border border-hairline bg-surface-subtle p-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-ink-muted">Suggested replacement</p>
                    <p className="mt-2 text-sm font-medium text-ink">{suggestedTrigger}</p>
                    {suggestedTriggerVariations.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-ink-secondary">Also match these phrases</p>
                        <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-ink-secondary">
                          {suggestedTriggerVariations.map((variation) => <li key={variation}>{variation}</li>)}
                        </ul>
                      </div>
                    )}
                    <div className="mt-4">
                      <Button
                        variant={decision?.action === 'edit' && decision.trigger === suggestedTrigger ? 'primary' : 'secondary'}
                        onClick={() => updateDecision({
                          findingId: finding._id,
                          action: 'edit',
                          documentId: broadSource.documentId,
                          trigger: suggestedTrigger,
                          triggerVariations: suggestedTriggerVariations,
                        })}
                      >
                        Use suggested trigger
                      </Button>
                    </div>
                  </div>
                )}
                <p className="text-sm text-ink-secondary">
                  {hasBroadSuggestion
                    ? 'Use the suggestion, edit the landmine yourself, or keep the current trigger as intentional.'
                    : 'Edit the landmine trigger above, or keep it as intentional.'}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant={decision?.action === 'mark_intentional' ? 'primary' : 'secondary'} onClick={() => updateDecision({ findingId: finding._id, action: 'mark_intentional' })}>Keep as intentional</Button>
                  <Button variant="tertiary" onClick={clearCurrentDecision}>Skip for now</Button>
                </div>
              </div>
            )}

            {finding.kind === 'contradiction' && decision?.action !== 'delete' && (
              <div className="space-y-3">
                <p className="text-sm text-ink-secondary">Edit or delete the entry that is wrong, or mark the difference as intentional.</p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant={decision?.action === 'mark_intentional' ? 'primary' : 'secondary'} onClick={() => updateDecision({ findingId: finding._id, action: 'mark_intentional' })}>Mark intentional</Button>
                  <Button variant="tertiary" onClick={clearCurrentDecision}>Skip for now</Button>
                </div>
              </div>
            )}

            {error && <div className="rounded-control border border-error bg-error-tint p-3 text-sm text-ink" role="alert">{error}</div>}
          </div>
        </div>
      </DialogContent>
      <DialogActions className="border-t border-hairline px-6 py-4" sx={{ justifyContent: 'space-between' }}>
        <p className="text-xs text-ink-muted">
          {selectedCount === 0
            ? 'No changes selected. Findings will stay open.'
            : `${selectedCount} of ${findings.length} findings selected. Unselected findings will stay open.`}
        </p>
        <div className="flex items-center gap-2">
          <Button variant="tertiary" onClick={onClose} disabled={applying}>Close</Button>
          <Button variant="primary" onClick={apply} disabled={applying || selectedCount === 0}>
            {applying ? 'Applying selected changes...' : `Apply all selected changes (${selectedCount})`}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default KbAuditReviewPanel;
